/**
 * TabManager - Manages tab references for cleanup purposes
 *
 * Usage:
 * - tabManager.openTab(url, key) - Opens a tab and stores reference
 * - tabManager.closeTab(key) - Closes a specific tab
 * - tabManager.closeAll() - Closes all managed tabs (useful for logout)
 * - tabManager.getTab(key) - Gets tab reference
 * - tabManager.isTabOpen(key) - Checks if tab is still open
 */
class TabManager {
	constructor() {
		this.tabs = new Map();
	}

	openTab(url, key, target = "_blank") {
		const tab = window.open(url, target);
		if (tab) {
			this.tabs.set(key, tab);
		}
		return tab;
	}

	// Open a tab keyed by name, or focus the existing one if it's still open.
	// Uses the key as the window name so the browser routes repeat opens to the
	// same tab. tab.focus() is best-effort: some browsers ignore it for tabs the
	// user did not just interact with.
	focusOrOpen(url, key) {
		const existing = this.tabs.get(key);
		if (existing && !existing.closed) {
			try {
				existing.focus();
			} catch {
				// Cross-origin or browser-policy denial; ignore.
			}
			return existing;
		}
		const tab = window.open(url, key);
		if (tab) {
			this.tabs.set(key, tab);
			try {
				tab.focus();
			} catch {
				// As above.
			}
		}
		return tab;
	}

	closeTab(key) {
		const tab = this.tabs.get(key);
		if (tab && !tab.closed) {
			tab.close();
		}
		this.tabs.delete(key);
	}

	closeAll() {
		this.tabs.forEach((tab) => {
			if (tab && !tab.closed) {
				tab.close();
			}
		});
		this.tabs.clear();
	}

	getTab(key) {
		return this.tabs.get(key);
	}

	hasTab(key) {
		return this.tabs.has(key);
	}

	isTabOpen(key) {
		const tab = this.tabs.get(key);
		return tab && !tab.closed;
	}
}

// Create a singleton instance
const tabManager = new TabManager();

export default tabManager;
